/**
 * Frame reducer for the terminal state machine — ported from the parked
 * `AgentProvider.tsx` and rewired onto `@paradigm/terminal-core` types.
 *
 * `handleFrame(frame, setState, options)` is the single entry point the
 * provider wires to `AgentWebSocket.onMessage`. It owns all gateway-frame →
 * chat-state translation: streaming deltas, structured `chat` events,
 * widgets / agent-specs, approvals, generative-UI render directives, app
 * register/remove, processing-state indicators, and `/model` echo
 * suppression. Per-session routing (runId → owning session + background
 * buffering) is injected via `options.routing` so the same handler works in
 * unit tests (single-session fallback) and in the live provider.
 */
import type {
  ApprovalRequest,
  ChatMessage,
  GatewayFrame,
  GatewayHistoryFraming,
  GatewayHistoryRow,
  MessageAttachment,
  UiRender,
} from '@paradigm/terminal-core';
import {
  adoptableRowTitle,
  attachmentsFromMediaPaths,
  applySubagentActivity,
  generateMessageId,
  isSafeAppUrl,
  parseSessionContextUsage,
  parseSubagentActivity,
  projectGatewayHistory,
  readGatewayRowContent,
  reconcileSessionContextUsage,
  sendKeyFromRow,
  TOOL_CALLS_WINDOW,
  TOOL_NAME_MAX_CHARS,
} from '@paradigm/terminal-core';
import type { Dispatch, SetStateAction } from 'react';

import type { AgentState, ProcessingState } from './contexts';

/**
 * Manifest for a custom application tab registered by the agent (via the
 * `app.register` event). The new core does not export this type, so the
 * minimal shape the handler needs is declared here and surfaced through the
 * `onAppRegister` plumbing.
 */
export interface AppManifest {
  readonly id: string;
  readonly name: string;
  readonly icon?: string;
  readonly url: string;
  readonly permissions: readonly string[];
}

/**
 * How a frame changes the in-flight indicator: a replacement value, `null` to
 * clear it, or a function of the current one.
 *
 * The function form exists because the indicator is per-session and a frame
 * can land in a session the user has switched away from, where the current
 * value lives in the provider's background stash rather than in `AgentState`.
 * A raw value cannot accumulate into that (counting reasoning characters for a
 * backgrounded run) or make a decision against it (ignoring an end-of-run
 * frame for a different run), so the routing layer applies the patch against
 * whichever of the two the frame is destined for.
 */
export type ProcessingPatch =
  | ProcessingState
  | null
  | ((prev: ProcessingState | null) => ProcessingState | null);

/** Resolves a {@link ProcessingPatch} against the value it is patching. */
export function applyProcessingPatch(
  patch: ProcessingPatch,
  prev: ProcessingState | null,
): ProcessingState | null {
  return typeof patch === 'function' ? patch(prev) : patch;
}

/**
 * Per-session routing for message-producing frames. When omitted (e.g. in
 * unit tests), all frames apply to whatever state `setState` exposes —
 * preserving the pre-routing single-session behaviour. The provider wires
 * this in for real so an in-flight run keeps writing to its original
 * session even after the user clicks `+ New chat`.
 */
export interface FrameRouting {
  readonly routeMessageFrame: (
    payload: { sessionKey?: string; runId?: string },
    reducer: (messages: readonly ChatMessage[]) => readonly ChatMessage[],
    activeExtra?: { processing?: ProcessingPatch },
  ) => void;
  readonly isCancelled: (runId: string | undefined) => boolean;
  /**
   * Record a run failure against the session that OWNS the run, so it stays
   * discoverable after a session switch instead of vanishing with a 5s toast.
   * The provider resolves the owner (and drops frames belonging to another
   * device's session, exactly as `routeMessageFrame` does).
   */
  readonly reportSessionError?: (
    payload: { sessionKey?: string; runId?: string },
    message: string,
    /**
     * The gateway's structured failure code. Kept beside the message because
     * it is the part that survives a harness swap: wording changes between
     * backends, a code is a contract.
     */
    code?: string,
  ) => void;
  readonly isFrameForActiveSession: (payload: {
    sessionKey?: string;
    runId?: string;
  }) => boolean;
  /**
   * Resolve a gateway session key — bare or `agent:<channel>:<id>` — to the
   * local session it names, or null when nothing here knows it. Sub-agent
   * subtasks are stored under the resolved id, which is also what bounds the
   * map to sessions this client knows.
   */
  readonly resolveSessionId?: (key: string) => string | null;
  /**
   * Signal that `runId` has reached a terminal state (done / error /
   * terminal `data.state`). The provider uses this hook to drop the
   * runId from its run-ownership map so it doesn't accumulate one entry
   * per run for the lifetime of the page.
   */
  readonly onRunEnd?: (event: RunEndEvent) => void;
  /**
   * Whether `runId` has ALREADY reached a terminal state. A late frame for a
   * finished run must not open anything the UI cannot then close: every
   * terminator is scoped to a `runId` the provider has since retired, so a
   * block created live here would shimmer for the rest of the session — and,
   * because a live block counts as in-flight work, would also suppress the
   * processing row and wedge the background-session buffer permanently.
   *
   * `undefined` asks about the run-less stream, which has no id to key on.
   * The provider keys those marks PER SESSION, resolving the owner from the
   * payload — hence the second argument — and clears the active chat's mark
   * when the user sends again. See the note on `endedRunIdsRef` for what that
   * scoping still does not settle.
   */
  readonly isRunEnded?: (
    runId: string | undefined,
    payload: RunFramePayload,
  ) => boolean;
  /**
   * Whether the agent's generated title may still replace this chat's name.
   *
   * False once the user has named the chat themselves. A rename is
   * `sessions.patch { label }`, and openclaw echoes the label on the next
   * push — so once the round trip completes the label outranks the generated
   * title on its own (`adoptableRowTitle`). This closes the window BEFORE it
   * completes: title generation finishes 2–4s after the first send, which is
   * exactly when someone renaming a brand-new chat is typing, and a push
   * carrying only `displayName` would otherwise undo it.
   *
   * Provider-side because the provider is what knows a rename happened. Not
   * wired ⇒ adoption is allowed, which is the right default for a caller with
   * no rename of its own to protect.
   */
  readonly mayRetitle?: (sessionId: string) => boolean;
}

/** The routing fields every frame carries, and all `isRunEnded` needs. */
export interface RunFramePayload {
  readonly sessionKey?: string;
  readonly runId?: string;
}

export interface RunEndEvent {
  readonly runId: string | undefined;
  /** The terminator frame, so the provider can resolve which chat it ends. */
  readonly payload: RunFramePayload;
  /**
   * True ONLY for the gateway's own end-of-run frame (`agent.done` /
   * `chat.done`). Every other terminator retires the run for `isRunEnded` but
   * must not release run OWNERSHIP: trailing frames still carry the runId,
   * and once its owner is forgotten they resolve to whichever chat is open —
   * clearing that chat's `processing` mid-run, and tripping the
   * foreign-session hydration pull.
   */
  readonly releasesOwnership: boolean;
}

export interface HandleFrameOptions {
  readonly generativeUiEnabled: boolean;
  /**
   * Called when the gateway dispatches an `app.register` event for a
   * custom application tab.
   */
  readonly onAppRegister?: (manifest: AppManifest) => void;
  /** Called when the gateway dispatches an `app.remove` event. */
  readonly onAppRemove?: (id: string) => void;
  readonly routing?: FrameRouting;
  /**
   * Runs whose frames must not reach the transcript — a `/model` the USER
   * typed, and whatever the gateway answers it with. Filled by the provider
   * when it claims such a run, and by `isModelSwitchEcho` below.
   *
   * The client no longer sends `/model` on anyone's behalf: model selection
   * is `sessions.patch { model }`, which starts no run and reaches no
   * transcript. What remains is the command a user can still type.
   *
   * Structural rather than a `Set` so the provider can hand over its BOUNDED
   * add; the recognition path has no eviction of its own. A `Set` satisfies
   * the shape, so a test may still pass one.
   */
  readonly suppressedModelEchoRunIds?: {
    has(runId: string): boolean;
    add(runId: string): void;
  };
  /**
   * Runs that have already reached a terminal signal, fed by the provider from
   * `onRunEnd`.
   *
   * A gateway can emit a frame for a run AFTER that run has ended — a trailing
   * state frame arriving a moment into the next turn is the common one. Those
   * frames must not touch the indicator: the row they would land on belongs to
   * a different run, and naming it after a finished one leaves it with an id
   * no future end frame will match.
   */
  readonly endedRunIds?: ReadonlySet<string>;
  /**
   * Run keys this client has sent and not yet seen finish.
   *
   * openclaw adopts the `chat.send` idempotency key as the run's `runId`, so a
   * frame carrying one of these is certainly OUR run. That is the only thing
   * the set is used for: deciding whether an unnamed row may take a frame's
   * runId as its own.
   *
   * It is a preference, never a requirement. A gateway that mints its own
   * runIds — which the provider's own comment says is possible and harmless —
   * would match nothing here, and the row simply stays unnamed, which
   * `endRunPatch` handles safely. Stamping the row with a send key outright
   * would instead give it an id no frame ever carries, and every terminator
   * would be refused: a permanent hang with the composer stuck on Stop, on
   * exactly the gateways this hedge exists for.
   *
   * When the set is absent or empty the row adopts whatever names it, which is
   * right for a run this client did not start (resumed from another device).
   */
  readonly pendingRunKeys?: ReadonlySet<string>;
  /**
   * True once this gateway has answered a `chat.send` with the key we sent it
   * — i.e. it adopts send keys as run ids rather than minting its own.
   *
   * Settled by the provider from that response, per send. It is NOT inferred
   * here: this reducer used to latch it off "a frame carried an id we hold",
   * which stopped meaning adoption once `pendingRunKeys` began holding the
   * gateway's own ids, and then read every minting run as proof of the
   * opposite.
   *
   * Until that is known, an unfamiliar runId is accepted as naming the row: on
   * a gateway that mints its own ids, refusing would leave the row unnamed
   * forever, and an unnamed row is clearable by any run that has not ended.
   * Once known, an unfamiliar id can be refused with confidence.
   */
  readonly gatewayAdoptsRunKeys?: boolean;
  /**
   * Called for connection-level errors surfaced as a transient toast rather
   * than attributed to a chat: orphan error responses with no matching pending
   * request (e.g. "gateway unreachable"). `handleFrame` sets `transientError`
   * on the state itself; the host wires this callback to the dismissal timer.
   *
   * Run failures do NOT come through here when `routing.reportSessionError` is
   * wired — they belong to a specific chat and must outlive a 5s timer, so
   * they are recorded per session instead.
   */
  readonly onTransientError?: (message: string, code?: string) => void;
  /**
   * The framing this host wraps outbound turns in, so a row echoed back can
   * be unwrapped to the text the user actually typed. Same value the
   * transcript read passes; see `GatewayHistoryFraming`.
   */
  readonly historyFraming?: GatewayHistoryFraming;
  /**
   * Which bubble on screen a `chat.send` key belongs to — the id the browser
   * minted for the message when it painted it, looked up by the key that went
   * out with the send.
   *
   * The lookup that lets the handler recognise the user's own message coming
   * back from the gateway instead of showing it a second time. Held by the
   * provider (bounded, a handful of recent sends), which is the only thing
   * that knows both halves.
   */
  readonly localSendKeys?: { get(sendKey: string): string | undefined };
}

const PROCESSING_STATE_LABELS: Readonly<Record<string, string>> = {
  thinking: 'Thinking',
  reasoning: 'Thinking',
  planning: 'Planning',
  running: 'Running',
  tool_use: 'Calling tool',
  tool_call: 'Calling tool',
  tool: 'Calling tool',
  searching: 'Searching',
  analyzing: 'Analyzing',
  streaming: 'Writing',
  writing: 'Writing',
};

/** `agent.data.state` values that mean "stop showing the indicator." */
const TERMINAL_PROCESSING_STATES: ReadonlySet<string> = new Set([
  'done',
  'complete',
  'completed',
  'finished',
  'idle',
  'ready',
]);

/**
 * Turn a raw gateway state string into a display-ready label.
 *
 * `tool_use-running ` → `Tool Use Running`
 *   1. `[_-]+` → ` `   collapse snake_case / kebab-case separators to spaces
 *   2. `\s+`   → ` `   collapse any runs of whitespace the above produced
 *   3. `.trim()`       drop leading/trailing whitespace
 *   4. `\b\w` → upper  title-case the first letter of every word
 */
function humanizeState(state: string): string {
  return state
    .replace(/[_-]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
    .replace(/\b\w/gu, (c) => c.toUpperCase());
}

function resolveProcessingLabel(state: string): string {
  const key = state.toLowerCase();
  return PROCESSING_STATE_LABELS[key] ?? humanizeState(state);
}

/**
 * True if `text` looks like the gateway's echo of a `/model <id>` switch
 * (e.g. `Model set to … for this session.` or a bare `/model …` echo). The
 * provider posts its own confirmation once the switch is verified against
 * the gateway, so the echoed bubble is dropped. Pure string detection — not
 * catalog data — so it lives in the handler rather than the host's model
 * capability. Gateway ERROR text for a rejected switch does not match and
 * still surfaces in chat.
 */
function isModelSwitchEcho(text: string): boolean {
  const trimmed = text.trimStart();
  if (trimmed.startsWith('/model')) return true;
  return /^\s*model\s+set\s+to\b|^\s*model\s+reset\s+to\s+default\b|^\s*switched\s+to\s+model\b/iu.test(
    text,
  );
}

/**
 * Two milliseconds after `Date.now()`, for a message appended in the same
 * synchronous handler that closes a bubble (a widget, an agent-spec render).
 *
 * Equal timestamps send `mergeMessages` to its id tie-break, which orders by
 * id STRING — and that resolves the wrong way at both ends: 'agent-spec-…'
 * sorts before 'assistant-…', and 'assistant-…' before 'user-…'. So a close
 * left on the raw clock still sorts an answer above the prompt that produced
 * it when a cached or replayed turn lands in one millisecond, and an append
 * left there sorts a render above the text it followed.
 *
 * One strict step each way fixes both: the close takes +1 (see `closedAt`),
 * the append +2. Nudging forward rather than back is what makes it safe —
 * there is nothing above the append to collide with, whereas the prompt sits
 * directly below the close.
 */
function appendedAfterClose(): number {
  return Date.now() + 2;
}

/**
 * Take every live bubble for `runId` (or all of them, with no runId) out of
 * its streaming state — `isStreaming` and `isThinking` alike.
 *
 * The gateway's `agent.done` / `chat.done` event is the canonical stream
 * terminator, but we can't rely on it — a dropped frame, a run-id flip, or a
 * skipped done event would leave the streaming dots animating (or a thinking
 * block shimmering) forever. Call this on every other concrete terminator
 * (terminal state, new widget, new approval, error, new streaming run) so the
 * UI self-corrects.
 *
 * A bubble closed for a NAMED run is also re-stamped to the moment it closed.
 * `timestamp` is set from the first delta, so without this the time revealed
 * on a finished turn is the moment the answer STARTED — minutes stale on a
 * long turn, and the exact value this UI stopped showing mid-turn for being
 * meaningless. The `chat final` path already gets an end-of-turn stamp from
 * the gateway (it builds a fresh message); this brings the three paths that
 * close a bubble in place — `agent.done`, a terminal state, `lifecycle.end` —
 * into line with it.
 *
 * Only for a named run, deliberately. The no-runId call is the "close whatever
 * is open, we cannot tell whose it is" path, and a stale bubble from an
 * earlier turn re-stamped to now would sort to the end of the transcript
 * (`mergeMessages` orders by timestamp). Where the close cannot be attributed,
 * neither can the completion time.
 */
function closeStreamingMessages(
  messages: readonly ChatMessage[],
  runId?: string,
): readonly ChatMessage[] {
  const isLive = (m: ChatMessage): boolean =>
    (m.isStreaming === true || m.isThinking === true) &&
    (runId == null || m.runId === runId);
  if (!messages.some(isLive)) return messages;
  // +1, not the raw clock: `mergeMessages` breaks a timestamp tie by id, and
  // 'assistant-…' < 'user-…', so a turn whose answer closes in the same
  // millisecond as its own prompt — a cached or replayed one — would sort the
  // answer above the question. See `appendedAfterClose` for the other end.
  const closedAt = Date.now() + 1;
  return messages.map((m) =>
    isLive(m)
      ? {
          ...m,
          isStreaming: false,
          isThinking: false,
          ...(runId != null ? { timestamp: closedAt } : {}),
        }
      : m,
  );
}

/**
 * End the run's tool activity. Deliberately NOT part of
 * `closeStreamingMessages`, which also runs mid-run (widgets, `ui.render`,
 * approvals) — clearing there made the next repeat frame for a still-running
 * call look like a second call, which is the overcount `applyToolCallUpdate`
 * rules out.
 *
 * Scoped like `closeStreamingMessages`: no runId clears EVERY live log, not
 * just the run-less one — `endToolActivity` reads a null runId the other way,
 * as the run-less stream. That is the terminator reading ("everything is
 * over"), and it keeps the two halves of `endRunMessages` agreeing: scoping
 * only this half would close another run's bubble while leaving its tool
 * header shimmering.
 */
function clearToolActivity(
  messages: readonly ChatMessage[],
  runId?: string,
): readonly ChatMessage[] {
  const isLive = (m: ChatMessage): boolean =>
    m.isToolActive === true && (runId == null || m.runId === runId);
  if (!messages.some(isLive)) return messages;
  return messages.map((m) => (isLive(m) ? { ...m, isToolActive: false } : m));
}

/**
 * A real end of run: close open bubbles AND end tool activity. Use at
 * terminators only; `closeStreamingMessages` alone everywhere else.
 */
function endRunMessages(
  messages: readonly ChatMessage[],
  runId?: string,
): readonly ChatMessage[] {
  return clearToolActivity(closeStreamingMessages(messages, runId), runId);
}

/**
 * Reasoning characters kept per message — a SLIDING window over the tail, not
 * a stop-appending cap.
 *
 * Nothing upstream bounds a run's reasoning. The provider's thinking budget is
 * per model REQUEST, and one run makes many: interleaved reasoning around each
 * tool call grows this same block (see `applyThinkingUpdate`), so a long
 * agentic run accumulates phase after phase into one string. Left unbounded
 * that string is re-encoded (`trimMessagesForPersistence`) and re-serialised
 * (`JSON.stringify` into localStorage) on EVERY streamed delta, which is
 * quadratic over a run — the wall is jank long before it is memory.
 *
 * The previous 20k bound kept the HEAD and stopped appending, which froze the
 * block mid-sentence while its header still shimmered "Thinking" — from the
 * reader's side, indistinguishable from the terminal having hung. Keeping the
 * TAIL instead means the text never stops moving, which is the signal that the
 * run is alive.
 *
 * The numbers match Hermes Agent, the closest comparable harness, which bounds
 * its own reasoning accumulator exactly this way (`turnController.ts`: at 80k,
 * `slice(-60_000)`). Deliberate parity: 60k is three times what the old cap
 * allowed through, and holding a known-good harness's ceiling is a better
 * starting point than a number we picked ourselves.
 *
 * Two constants rather than one because the slide is HYSTERETIC. An exact
 * window would re-slice a 60k string on every delta once the trace passed it;
 * letting the body run to 80k before cutting back to 60k pays that copy once
 * per 20k of new reasoning instead. The cost is a 80k rather than 60k
 * high-water mark, which is ~160 KB of UTF-16 — immaterial next to the copying
 * it avoids.
 *
 * `trimMessagesForPersistence` in the core store still enforces the
 * per-session byte budget on top of this.
 */
export const THINKING_WINDOW_CHARS = 60_000;
/** High-water mark that triggers a slide back to `THINKING_WINDOW_CHARS`. */
export const THINKING_SLIDE_AT_CHARS = 80_000;

/**
 * Sentinel leading the head-of-trace marker.
 *
 * The marker is parsed back off a stored trace to keep the window's dropped
 * count cumulative (see `splitTrimmed`), and the head of that trace is
 * MODEL-CONTROLLED text: a snapshot that fits the window is stored verbatim.
 * Reasoning that merely quoted the marker's wording would otherwise be parsed
 * as one — inventing a dropped count, eating the model's real first line, and
 * corrupting the exact-offset test in `subsumes` into opening the duplicate
 * block per phase this whole change exists to prevent.
 *
 * U+2060 WORD JOINER is the guard: zero-width and non-rendering, so the
 * visible marker is unchanged, and not something free-form reasoning emits as
 * the very first character of a trace.
 */
const THINKING_TRIM_SENTINEL = '⁠';

/** Head-of-trace marker written by `appendThinking` once the window slides. */
const THINKING_TRIM_RE = new RegExp(
  `^${THINKING_TRIM_SENTINEL}\\[… (\\d+) earlier characters trimmed\\]\\n`,
  'u',
);

function trimMarker(dropped: number): string {
  return `${THINKING_TRIM_SENTINEL}[… ${dropped} earlier characters trimmed]\n`;
}

/**
 * Split a stored trace into the characters already dropped off its head and
 * the text still held. Only a marker carrying `THINKING_TRIM_SENTINEL` counts
 * as one this module wrote; anything else is the model's own text and is left
 * whole.
 */
function splitTrimmed(trace: string): { dropped: number; body: string } {
  const match = THINKING_TRIM_RE.exec(trace);
  if (match == null) return { dropped: 0, body: trace };
  return {
    dropped: Number(match[1]),
    body: trace.slice(match[0].length),
  };
}

/**
 * Append a reasoning delta, sliding the tail back to `THINKING_WINDOW_CHARS`
 * once the body passes `THINKING_SLIDE_AT_CHARS`, and marking how much fell
 * off the head. Below the trigger this is a plain concatenation, so the
 * ordinary turn pays nothing for the machinery.
 */
function appendThinking(existing: string, delta: string): string {
  const { dropped, body } = splitTrimmed(existing);
  const next = body + delta;
  if (next.length <= THINKING_SLIDE_AT_CHARS) {
    return dropped === 0 ? next : `${trimMarker(dropped)}${next}`;
  }
  let cut = next.length - THINKING_WINDOW_CHARS;
  // Cutting at a UTF-16 boundary can leave the LOW half of a surrogate pair
  // leading the window — a U+FFFD glyph on any trace containing an emoji.
  // Drop the orphan rather than render it.
  const lead = next.charCodeAt(cut);
  if (lead >= 0xdc00 && lead <= 0xdfff) cut += 1;
  return `${trimMarker(dropped + cut)}${next.slice(cut)}`;
}

/**
 * States that mean a tool is running. Its own set rather than a label check:
 * the "Calling tool" label is a display string several states can share.
 */
const TOOL_PROCESSING_STATES: ReadonlySet<string> = new Set([
  'tool_use',
  'tool_call',
  'tool',
]);

/**
 * Declared in core, because the load path re-enforces the same bounds on logs
 * from sync docs and older blobs. Re-exported here for this module's callers.
 */
export { TOOL_CALLS_WINDOW, TOOL_NAME_MAX_CHARS };

/**
 * The tool name a state frame reports. `toolName`/`tool` are taken at face
 * value; `text` only when it looks like an identifier, so a prose status line
 * cannot become a row claiming to be a tool. (The in-flight row keeps its own
 * broader `text` fallback — a transient label may be a sentence.)
 */
/**
 * A bubble's attachments plus the ones a later frame named, without repeats.
 *
 * A turn can produce a second file after its first delta, and a re-sent frame
 * repeats the ones it already named — so this is a union keyed on how the file
 * is addressed, not an append.
 */
function mergeAttachments(
  existing: readonly MessageAttachment[] | undefined,
  produced: readonly MessageAttachment[],
): readonly MessageAttachment[] {
  if (existing == null || existing.length === 0) return produced;
  const known = new Set(
    existing.map((a) => a.sourceUrl ?? a.previewUrl ?? a.fileName ?? ''),
  );
  const fresh = produced.filter(
    (a) => !known.has(a.sourceUrl ?? a.previewUrl ?? a.fileName ?? ''),
  );
  return fresh.length === 0 ? existing : [...existing, ...fresh];
}

function toolNameFromFrame(data: {
  toolName?: string;
  tool?: string;
  text?: string;
}): string | undefined {
  // `data` is an unchecked cast: a numeric `text` passes both length tests
  // (`.length` is undefined) and stringifies through the regex, recording
  // `{ name: 123 }` — which the all-or-nothing sanitizer drops the whole log
  // over on reload.
  const named = data.toolName ?? data.tool;
  if (typeof named === 'string' && named.length > 0)
    return named.slice(0, TOOL_NAME_MAX_CHARS);
  const { text } = data;
  if (typeof text !== 'string' || text.length === 0) return undefined;
  if (text.length > TOOL_NAME_MAX_CHARS) return undefined;
  if (/\s/u.test(text)) return undefined;
  return text;
}

/**
 * The tool log for `runId`, or -1 to start one. A run-less scan stops at the
 * first non-assistant message, or it would append to a log from another turn;
 * a runId-bearing one must not, since a queued user message can land mid-run.
 */
function findToolLogRecord(
  messages: readonly ChatMessage[],
  runId: string | undefined,
): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]!;
    if (message.role !== 'assistant') {
      if (runId != null) continue;
      return -1;
    }
    const ownedByRun =
      runId != null ? message.runId === runId : message.runId == null;
    if (ownedByRun && message.toolCalls != null) return i;
  }
  return -1;
}

/**
 * Record a tool call, creating the log on first contact.
 *
 * The gateway re-sends `tool_use` for the whole call, so appending per frame
 * would report one three-second `Bash` call as a hundred. A frame is a repeat
 * only when the log is already active AND names the same tool; `isToolActive`
 * is what records that, cleared by `endToolActivity` on the next non-tool
 * state.
 *
 * The cost: two identical calls back to back with no state between them merge
 * into one row. There is no per-call id to tell them apart, so the count
 * undercounts rather than overcounts — barring a run-less terminator landing
 * mid-call, which clears the flag for every run (see `clearToolActivity`).
 * Goes away once the gateway sends per-call events.
 */
function applyToolCallUpdate(
  messages: readonly ChatMessage[],
  runId: string | undefined,
  name: string,
  timestamp: number,
): readonly ChatMessage[] {
  const index = findToolLogRecord(messages, runId);

  if (index === -1) {
    const previous = messages[messages.length - 1];
    return [
      ...messages,
      {
        id: generateMessageId('tools'),
        role: 'assistant' as const,
        content: '',
        // Floored one millisecond past the message it follows, for the reason
        // `applyThinkingUpdate` gives at length: `mergeMessages` re-sorts the
        // transcript by timestamp on every merge and breaks ties by id, and
        // 'assistant-…' beats 'tools-…', so a log created in the same
        // millisecond as its own turn would sort below the answer it explains.
        timestamp:
          previous != null
            ? Math.max(timestamp, previous.timestamp + 1)
            : timestamp,
        toolCalls: [{ name }],
        isToolActive: true,
        runId,
      },
    ];
  }

  const current = messages[index]!;
  const calls = current.toolCalls ?? [];
  const last = calls[calls.length - 1];
  // The repeat case — by far the most frequent frame this function sees, and
  // the one that must not churn the array: returning `messages` unchanged is
  // what keeps a tool call that runs for seconds from re-rendering the
  // transcript on every gateway tick.
  if (current.isToolActive === true && last?.name === name) return messages;

  const appended = [...calls, { name }];
  const overflow = Math.max(0, appended.length - TOOL_CALLS_WINDOW);
  return [
    ...messages.slice(0, index),
    {
      ...current,
      toolCalls: overflow > 0 ? appended.slice(overflow) : appended,
      ...(overflow > 0
        ? { toolCallsDropped: (current.toolCallsDropped ?? 0) + overflow }
        : {}),
      isToolActive: true,
    },
    ...messages.slice(index + 1),
  ];
}

/**
 * The other half of the counting rule: this clear is what lets the next frame
 * naming the same tool count as a second call. Identity-stable when there is
 * nothing to clear, which is the common case.
 */
function endToolActivity(
  messages: readonly ChatMessage[],
  runId: string | undefined,
): readonly ChatMessage[] {
  const index = findToolLogRecord(messages, runId);
  if (index === -1) return messages;
  const current = messages[index]!;
  if (current.isToolActive !== true) return messages;
  return [
    ...messages.slice(0, index),
    { ...current, isToolActive: false },
    ...messages.slice(index + 1),
  ];
}

/**
 * End the live thinking block for `runId` (if any) without touching its text,
 * so it collapses to its one-line summary. Called when the run's first
 * ANSWER token lands: the model has stopped reasoning and started replying,
 * which is the collapse trigger — the run terminators go through
 * `closeStreamingMessages` instead.
 */
function endThinking(
  messages: readonly ChatMessage[],
  runId: string | undefined,
): readonly ChatMessage[] {
  // `runId == null` means the RUN-LESS stream, not "every run" — mirroring
  // `findThinkingRecord`. Treating it as a wildcard let a run-less answer
  // delta collapse an identified run's live reasoning out from under the
  // reader: the same cross-run damage `replaceRunMessages` avoids.
  const isLive = (m: ChatMessage): boolean =>
    m.isThinking === true &&
    (runId != null ? m.runId === runId : m.runId == null);
  if (!messages.some(isLive)) return messages;
  return messages.map((m) => (isLive(m) ? { ...m, isThinking: false } : m));
}

/**
 * Whether a snapshot's text continues what a block already holds — i.e. it is
 * a cumulative whole-turn trace rather than a fresh per-block one.
 *
 * A windowed block holds a SUBSTRING of what the emitter has sent: not a
 * prefix (the emitter has moved past it) and not a suffix (the window trails
 * the live edge by whatever arrived after the last slide). A plain prefix test
 * therefore fails on every windowed block and sends each later phase down the
 * create path, which is the full-length duplicate-block-per-phase bug — one
 * visually identical block per interleaved phase, each spending the
 * per-session persist budget that older turns' reasoning needs.
 *
 * The marker carries the offset, so the test is exact: a cumulative snapshot
 * must have the block's own text sitting at the position it was cut from. With
 * nothing dropped this reduces to the prefix test it replaces.
 *
 * The legacy branch covers a trace persisted by the PREVIOUS build, which
 * stored `head + '…'` with no marker. That ellipsis is ours, not the
 * emitter's, so the exact test can never match once the trace extends past the
 * old cap — and the block would fall to the create path, resurrecting the very
 * duplicate-block bug this function exists to prevent, for any run still
 * streaming across an upgrade (the PWA reloads mid-run on deploy, and history
 * is restored from localStorage). Tried only after the exact test fails, so a
 * live trace whose own text happens to end in an ellipsis is unaffected.
 */
function subsumes(text: string, existing: string): boolean {
  const { dropped, body } = splitTrimmed(existing);
  if (text.slice(dropped, dropped + body.length) === body) return true;
  return (
    dropped === 0 && body.endsWith('…') && text.startsWith(body.slice(0, -1))
  );
}

/**
 * Find the thinking record this frame should fold into, or -1 to start a new
 * block. Reuse is deliberately narrow, because "the run's block" is only
 * reliably identifiable when the frame carries a `runId`:
 *
 *  - With a `runId`, an already-COLLAPSED block is reusable for an
 *    incremental delta. That is interleaved thinking — reasoning resuming
 *    around a tool call, after the first answer token already ended the
 *    block — and it belongs in the block it continues rather than in a new
 *    one stranded below the answer.
 *  - WITHOUT a `runId` there is nothing to bound the search: every past
 *    turn's block also has `runId == null`, so reusing a collapsed one fuses
 *    this turn's reasoning into the previous turn's block, or (after a
 *    reload, where persistence has cleared the flags) into one from another
 *    day. Run-less frames therefore reuse only a LIVE block, and their scan
 *    stops at the first non-assistant message — the user turn that opened
 *    this run, below which nothing can belong to it. A `runId` needs no such
 *    bound (it cannot cross turns) and must not have one: a queued user
 *    message can land mid-run, and stopping there would open a second block
 *    for a run whose first is still live.
 *  - A `replace` frame carries a whole trace rather than an increment, and
 *    the two emitter shapes disagree about *whose*: some re-send the whole
 *    turn, some only the current block. `subsumes` tells them apart — a
 *    whole-turn snapshot always contains what the block already holds as a
 *    prefix. Continue the block when it does; start a new one when it
 *    doesn't, which is the per-block emitter beginning a new phase.
 */
function findThinkingRecord(
  messages: readonly ChatMessage[],
  runId: string | undefined,
  mode: 'append' | 'replace',
  text: string,
): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]!;
    if (message.role !== 'assistant') {
      if (runId != null) continue;
      return -1;
    }
    const ownedByRun =
      runId != null ? message.runId === runId : message.runId == null;
    if (!ownedByRun || message.thinking == null) continue;
    if (message.isThinking === true) return i;
    // Collapsed. Only a runId-bearing frame may continue one, and a snapshot
    // only when its text subsumes what the block already holds.
    if (runId == null) return -1;
    if (mode === 'append') return i;
    return subsumes(text, message.thinking) ? i : -1;
  }
  return -1;
}

/**
 * Fold reasoning into the run's thinking message, creating it on first
 * contact. `mode` is `'append'` for an incremental delta and `'replace'` when
 * the frame carries the whole trace (see the caller for which is which).
 *
 * The block is deliberately its OWN message rather than a field on the
 * answer bubble: reasoning arrives BEFORE any answer text exists, and
 * keeping it separate means `updateStreamingMessage` (which matches on
 * `isStreaming`) never mistakes it for the answer bubble to append to, and
 * the `/model` echo detection still sees the answer's first delta as a
 * first delta.
 */
function applyThinkingUpdate(
  messages: readonly ChatMessage[],
  runId: string | undefined,
  text: string,
  mode: 'append' | 'replace',
  timestamp: number,
  runEnded: boolean,
): readonly ChatMessage[] {
  const index = findThinkingRecord(messages, runId, mode, text);

  if (index === -1) {
    const thinking = appendThinking('', text);
    if (thinking.length === 0) return messages;
    const previous = messages[messages.length - 1];
    return [
      ...messages,
      {
        id: generateMessageId('thinking'),
        role: 'assistant' as const,
        content: '',
        // A FLOOR of one millisecond after the message it follows, so the
        // block can never sort before its own turn through `mergeMessages`,
        // which re-sorts the whole transcript by timestamp on every sync
        // merge and breaks ties by id (`assistant-…` beats `thinking-…`). This
        // is NOT immunity to clock skew between this client and the gateway
        // that stamps the answer bubbles — nothing here can buy that — it
        // only stops the block losing to its own turn.
        //
        // A FLOOR rather than an override, so the clock wins in the ordinary
        // case and the anchor engages only where it has to: a same-millisecond
        // neighbour, and a neighbour stamped AHEAD of the clock. An earlier
        // version abandoned the anchor entirely in the second case, which
        // broke the ordering it exists for — a second block created after a
        // gateway-stamped answer from a fast clock sorted above that answer.
        // The cost of the floor is that a skewed neighbour's stamp does
        // propagate forward; relative order survives, since every later record
        // anchors the same way.
        timestamp:
          previous != null
            ? Math.max(timestamp, previous.timestamp + 1)
            : timestamp,
        thinking,
        // A late frame for a run that has already ended opens the block
        // COLLAPSED: every terminator is scoped to a runId the provider has
        // retired, so a live one here could never be closed — and a live
        // block counts as in-flight work, so it would suppress the processing
        // row and wedge the background-session buffer for good.
        isThinking: !runEnded,
        runId,
      },
    ];
  }

  const current = messages[index]!;
  const thinking =
    mode === 'append'
      ? appendThinking(current.thinking ?? '', text)
      : appendThinking('', text);
  // Identity-stable when nothing changed: a replace frame that repeats the
  // same cumulative text must not churn the array and re-render the
  // transcript on every 150ms gateway tick.
  if (thinking === current.thinking) return messages;
  // A collapsed block that is being continued stays collapsed. Re-marking it
  // live would yank a trace the reader had finished with back open under
  // them, and a frame arriving after the run's terminator would have nothing
  // left to close it. The processing row covers "still working" meanwhile.
  return [
    ...messages.slice(0, index),
    { ...current, thinking },
    ...messages.slice(index + 1),
  ];
}

/**
 * Close only the RUN-LESS live messages. Used where a run-less first delta
 * starts a new bubble: the unscoped `closeStreamingMessages` would close
 * every other run's live message too, which is the cross-run damage
 * `replaceRunMessages` and `endThinking` are both careful to avoid. The
 * unscoped form stays for terminators, where "everything is over" is exactly
 * what the frame means.
 */
function closeRunlessMessages(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  const isLive = (m: ChatMessage): boolean =>
    m.runId == null &&
    (m.isStreaming === true ||
      m.isThinking === true ||
      // The only clear on the delta path, before any terminator: for a stale
      // run-less log behind a later user turn, `findToolLogRecord`'s run-less
      // scan stops at that message and `endToolActivity` never reaches it.
      // (A run-less terminator would also clear it — `clearToolActivity`
      // treats a null runId as a wildcard.)
      m.isToolActive === true);
  if (!messages.some(isLive)) return messages;
  return messages.map((m) =>
    isLive(m)
      ? { ...m, isStreaming: false, isThinking: false, isToolActive: false }
      : m,
  );
}

function updateStreamingMessage(
  messages: readonly ChatMessage[],
  runId: string | undefined,
  update: (message: ChatMessage) => ChatMessage,
): readonly ChatMessage[] | null {
  const index = messages.findIndex(
    (m) =>
      m.role === 'assistant' &&
      m.isStreaming === true &&
      (runId == null ? m.runId == null : m.runId === runId),
  );

  if (index === -1) return null;

  return [
    ...messages.slice(0, index),
    update(messages[index]!),
    ...messages.slice(index + 1),
  ];
}

function replaceRunMessages(
  messages: readonly ChatMessage[],
  runId: string | undefined,
  replacements: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (runId == null) return [...endRunMessages(messages), ...replacements];

  const next: ChatMessage[] = [];
  let inserted = false;
  // Files the run already showed, so the terminator does not throw them away.
  //
  // A final REPLACES the run's bubbles, and it carries only the attachments in
  // its own content blocks — while a file the agent produced is named on a
  // streaming frame (`data.mediaUrls`) and merged onto the bubble as it
  // arrives. So the picture appeared during the turn and vanished the instant
  // it ended. This repo's own harness emits exactly that pair — an assistant
  // delta then a chat `final` — which makes it the canonical shape rather than
  // an edge case.
  //
  // Carried rather than re-derived: the frames that named these files are
  // gone by now, and the bubble being dropped is the only remaining record.
  // Collected in a PASS OF ITS OWN, before anything is built: the bubble
  // holding the file can sit after the point the replacement is inserted at,
  // and accumulating as we go would then miss it.
  let carried: readonly MessageAttachment[] = [];
  for (const message of messages) {
    if (message.role !== 'assistant' || message.runId !== runId) continue;
    // The thinking block and the tool log survive a final, so their files —
    // if a shape ever carries any — are not being dropped and must not be
    // copied onto the answer as well.
    if (message.thinking != null || message.toolCalls != null) continue;
    if (message.attachments != null && message.attachments.length > 0) {
      carried = mergeAttachments(carried, message.attachments);
    }
  }

  // Onto the FIRST replacement only. A final can insert several bubbles (an
  // agent-spec render inserts its preamble and the spec), and a file the run
  // showed once belongs on one of them, not on each.
  const withCarried = (
    replacement: ChatMessage,
    index: number,
  ): ChatMessage => {
    if (index !== 0 || carried.length === 0) return replacement;
    const merged = mergeAttachments(replacement.attachments, carried);
    return merged === replacement.attachments
      ? replacement
      : { ...replacement, attachments: merged };
  };

  for (const message of messages) {
    const isSameRun = message.role === 'assistant' && message.runId === runId;
    // Another run's message is passed through UNTOUCHED. Ending its thinking
    // block here would collapse a concurrent run's live reasoning out from
    // under the reader on this run's `final` — the same cross-run damage the
    // adjacent `isStreaming` handling is careful to avoid.
    if (!isSameRun) {
      next.push(message);
      continue;
    }
    // The thinking block and tool log are sibling records, not drafts of the
    // answer, so the final text replaces the bubbles and leaves both standing.
    if (message.thinking != null || message.toolCalls != null) {
      next.push(
        message.isThinking === true || message.isToolActive === true
          ? { ...message, isThinking: false, isToolActive: false }
          : message,
      );
      continue;
    }

    if (!inserted) {
      next.push(...replacements.map(withCarried));
      inserted = true;
    }
  }

  if (!inserted) next.push(...replacements.map(withCarried));

  return next;
}

export function handleFrame(
  frame: GatewayFrame,
  setState: Dispatch<SetStateAction<AgentState>>,
  options: HandleFrameOptions = { generativeUiEnabled: false },
) {
  // Fallbacks let `handleFrame` keep working without per-session routing
  // (unit tests, etc.) — in that mode every frame applies to current state.
  const routeMessageFrame: FrameRouting['routeMessageFrame'] =
    options.routing?.routeMessageFrame ??
    ((_payload, reducer, activeExtra) => {
      setState((prev) => {
        const next = reducer(prev.messages);
        if (activeExtra == null || !('processing' in activeExtra)) {
          return next === prev.messages ? prev : { ...prev, messages: next };
        }
        const processing = applyProcessingPatch(
          activeExtra.processing ?? null,
          prev.processing,
        );
        if (next === prev.messages && processing === prev.processing) {
          return prev;
        }
        return { ...prev, messages: next, processing };
      });
    });
  /** True for a frame belonging to a run that has already finished. */
  const isEndedRun = (runId: string | undefined): boolean =>
    runId != null && options.endedRunIds?.has(runId) === true;

  /**
   * Whether an unnamed row may take this frame's runId as its own.
   *
   * Only a run this client started, when we have any to compare against. A
   * concurrent run — a cron turn, another client's keyless frame — landing in
   * the window between a send and its first frame would otherwise name the
   * row, after which the user's own end frames are refused as foreign and the
   * row sticks with the composer pinned to Stop. `endedRunIds` cannot help
   * there: that run has not ended.
   */
  const namesOurRun = (runId: string | undefined): boolean => {
    if (runId == null) return false;
    const pending = options.pendingRunKeys;
    if (pending == null || pending.size === 0) return true;
    // Ours: the provider claimed this run when the gateway named it. NOT
    // evidence about key adoption — the set holds the gateway's own ids now,
    // so every minting run looked like adoption and the latch went permanently
    // true. The provider settles that at ack time from ground truth.
    if (pending.has(runId)) return true;
    // Not one of ours — but only refuse if we have reason to think this
    // gateway would have used ours. Keying off "the set is non-empty" made the
    // hedge one-way and broke it on exactly the id-minting builds it exists
    // for: after the first send the set is permanently non-empty and
    // permanently non-matching, so no frame could ever name the row again. An
    // unnamed row is safe from HANGS, but it is clearable by any run that has
    // not ended, so a concurrent cron turn's `final` took it down mid-turn.
    //
    // Note what this does NOT conclude: an unfamiliar id is not evidence that
    // the gateway mints. It is equally a cron turn, another client, or another
    // tab. Latching "mints" off it read a bystander's frame as evidence about
    // OUR runs — the same conflation as the poisoned row and the cross-session
    // cancel — and Stop then narrowed to the newest key on openclaw.
    return options.gatewayAdoptsRunKeys !== true;
  };

  /**
   * The patch an end-of-run frame applies: end the row, if this frame is for
   * the run the row is showing.
   *
   * A row that names a run is ended only by that run. A row that does NOT —
   * the one a local send raises before any frame has named it — is ended by
   * any run except one already known to be over: treating "no id" as an
   * unconditional match let the previous turn's trailing terminator clear the
   * turn that had just started.
   *
   * Built EAGERLY, and that is the whole point of it being a factory. The
   * patch runs inside a `setState` updater, which React batches until flush,
   * while `onRunEnd` fires synchronously right after this call site. A patch
   * that asked "has this run ended?" when it RAN would always be told yes —
   * by the very frame it belongs to — and would refuse to clear the row it
   * exists to clear. Asking now, before the frame reports itself, makes the
   * answer independent of when React gets round to running it.
   *
   * Every test that applies updaters synchronously sees the opposite order and
   * cannot catch this, which is both harnesses and jsdom. Hence the deferred
   * -flush test next to the others.
   */
  const endRunPatch = (runId: string | undefined): ProcessingPatch => {
    const runAlreadyEnded = isEndedRun(runId);
    return (prev) => {
      if (prev == null) return null;
      // A frame with no runId at all still matches: these are a safety net,
      // and a build that omits runIds has to be able to stop the clock.
      if (runId == null) return null;
      if (prev.runId == null) return runAlreadyEnded ? prev : null;
      return prev.runId === runId ? null : prev;
    };
  };
  /**
   * `endRunPatch` for a MUTED run: may it clear an UNNAMED row?
   *
   * A user-typed `/model` IS the turn that row was raised for, and nothing
   * else will ever clear it. A seed is written under a row raised for the
   * message about to be sent. Ownership separates them (`sendMessage` claims
   * into `pendingRunKeys`, a seed never is); namelessness does not.
   */
  const endMutedRunPatch = (runId: string | undefined): ProcessingPatch => {
    const pending = options.pendingRunKeys;
    // No set wired is "no information" — keep `endRunPatch`'s answer.
    const ownsTheTurn =
      runId != null && (pending == null || pending.has(runId));
    return (prev) => {
      if (prev == null) return null;
      if (runId == null) return null;
      if (prev.runId == null) return ownsTheTurn ? null : prev;
      return prev.runId === runId ? null : prev;
    };
  };
  const isCancelled: FrameRouting['isCancelled'] =
    options.routing?.isCancelled ?? (() => false);
  const isFrameForActiveSession: FrameRouting['isFrameForActiveSession'] =
    options.routing?.isFrameForActiveSession ?? (() => true);
  const resolveSessionId: (key: string) => string | null =
    options.routing?.resolveSessionId ?? (() => null);
  const mayRetitle: (sessionId: string) => boolean =
    options.routing?.mayRetitle ?? (() => true);
  const onRunEnd: (event: RunEndEvent) => void =
    options.routing?.onRunEnd ?? (() => undefined);
  const isRunEnded: (
    runId: string | undefined,
    payload: RunFramePayload,
  ) => boolean = options.routing?.isRunEnded ?? (() => false);
  /**
   * Retire a run. The whole frame goes through, because a terminator with no
   * `runId` speaks for a session's entire run-less stream and only the
   * provider can say which session that is — an earlier attempt gated this on
   * "is the frame for the active chat", which stopped a background terminator
   * silencing the open chat but left a background run's own late frames
   * opening a LIVE block inside its buffer, which then never flushed.
   *
   * `releasesOwnership` is false by default: only the gateway's own
   * end-of-run frame may forget who owns the runId.
   */
  const recordRunEnd = (
    payload: RunFramePayload,
    releasesOwnership = false,
  ): void => {
    onRunEnd({ runId: payload.runId, payload, releasesOwnership });
  };

  /**
   * A run failed. Close any half-streamed bubble and clear the processing
   * indicator for the OWNING session, then record the failure against that
   * session via `routing.reportSessionError` — the production path.
   *
   * Deliberately NOT a transcript bubble (infrastructure failures are not
   * conversation content and must not sync as messages) and NOT a transient
   * toast: `transientError` is a single global scalar with no session
   * attribution, so it blamed whatever chat happened to be open for a
   * background run's failure, and a 5s timer lost the error entirely for
   * anyone who looked away mid-run. Per-session and persistent fixes both.
   *
   * Without routing (unit tests / single-session mode) there is only one chat
   * to speak of, so it falls back to `transientError` + `onTransientError`.
   *
   * Shared by the `agent` and `chat` error branches, which were byte-identical.
   */
  const surfaceErrorFrame = (
    payload: { sessionKey?: string; runId?: string },
    errorMessage: string,
    errorCode?: string,
  ): void => {
    routeMessageFrame(
      payload,
      (messages) => endRunMessages(messages, payload.runId),
      { processing: endRunPatch(payload.runId) },
    );
    const reportSessionError = options.routing?.reportSessionError;
    if (reportSessionError != null) {
      // Per-session and persistent: a failure in a chat the user is not
      // looking at must still be there when they return, and one in the chat
      // they ARE looking at must not evaporate while they look away.
      reportSessionError(payload, errorMessage, errorCode);
    } else {
      // No routing wired (single-session mode): the one state this reducer can
      // see IS the owning chat, so the transient surface is the whole story.
      setState((prev) =>
        prev.transientError === errorMessage
          ? prev
          : { ...prev, transientError: errorMessage },
      );
      options.onTransientError?.(errorMessage, errorCode);
    }
    recordRunEnd(payload);
  };

  /**
   * A muted run still owes its terminator duties. Removing rather than
   * closing the bubble is the part that matters: `closeStreamingMessages`
   * leaves the message in place, and one message loses the landing layout.
   */
  const retireMutedRun = (
    payload: { sessionKey?: string; runId?: string; state?: string },
    runId: string | undefined,
  ): void => {
    routeMessageFrame(
      payload,
      (messages) => {
        if (runId == null) return messages;
        const kept = messages.filter(
          (m) =>
            !(
              m.role === 'assistant' &&
              m.runId === runId &&
              (m.isStreaming === true || m.thinking != null)
            ),
        );
        // Identity when nothing was removed, as `closeStreamingMessages` and
        // `endThinking` do: a fresh array rewrites a background chat.
        return kept.length === messages.length ? messages : kept;
      },
      { processing: endMutedRunPatch(runId) },
    );
    if (payload.state === 'final') recordRunEnd(payload);
  };

  if (frame.type === 'event') {
    const evt = frame;

    // Context-window usage. The gateway pushes `sessions.changed` at run start
    // and again once the run's transcript is persisted (we opt in once, via
    // `sessions.subscribe`), with the session row's token fields flattened
    // onto the payload. Read ONLY for the active chat: a background run in
    // another session would otherwise repaint the footer with a window that
    // isn't the one the user is looking at.
    //
    // Routed through `isFrameForActiveSession` rather than a bare sessionKey
    // compare so it inherits the same foreign-session guard every other frame
    // gets. A payload without usable numbers is dropped, not applied — see
    // `parseSessionContextUsage`.
    if (evt.event === 'sessions.changed') {
      const payload = evt.payload as { sessionKey?: string } | undefined;
      const sessionKey = payload?.sessionKey;
      // A keyless payload is NOT treated as "the active session" here, unlike
      // the message-producing frames: those need the last-send fallback to
      // place the opening frames of a locally started run, whereas this event
      // always names its session. Without the key there is nothing to check it
      // against, and a wrong reading here is a wrong number on screen.
      //
      // `runId` is deliberately not forwarded: `sessions.changed` reports the
      // GATEWAY's run id (the client's own is `clientRunId`), and the routing
      // helper would record it into the run-ownership map — where nothing ever
      // retires it, since `onRunEnd` only ever sees client run ids.
      // Typed rather than trusted: this is unvalidated gateway input, and the
      // routing helper below splits it on `:`.
      if (payload == null || typeof sessionKey !== 'string' || !sessionKey) {
        return;
      }

      // Sub-agent subtask count. A child's row arrives under the CHILD's key,
      // which matches no chat this client knows and is dropped by the guard
      // below — so this runs above that guard, and resolves the chat from the
      // row's own `spawnedBy`. The usage read stays below it: a child's token
      // figures belong to the child.
      //
      // Counted for EVERY chat on this connection, not just the one on
      // screen: the gateway pushes them all, and "chat B has 3 subtasks
      // running" stays true while the user reads chat A.
      const activity = parseSubagentActivity(payload);
      const running = activity?.running;
      if (activity != null && running != null) {
        // A row whose own key resolves to a chat this client knows is that
        // chat, not a subtask under it. `parseSubagentActivity` catches the
        // exact self-parent echo; only here can the namespaced form
        // (`agent:main:<id>` naming `<id>` as its parent) be resolved.
        const isKnownChatItself = resolveSessionId(activity.key) != null;
        // Attribution is required to START counting a child, but not to stop:
        // the fold removes a key wherever it is tracked, so an end push that
        // omits `spawnedBy` cannot pin the count.
        const parentId =
          activity.parentKey == null
            ? null
            : resolveSessionId(activity.parentKey);
        if (!isKnownChatItself && (parentId != null || !running)) {
          setState((prev) => {
            const runningSubtasks = applySubagentActivity(
              prev.runningSubtasks,
              { key: activity.key, parentId, running },
              Date.now(),
            );
            return runningSubtasks === prev.runningSubtasks
              ? prev
              : { ...prev, runningSubtasks };
          });
        }
      }

      // The chat's NAME, when the push carries one worth taking (a label, or
      // openclaw's generated title on a title-scoped chat — see
      // `adoptableRowTitle`). This is how a dynamic title arrives: the utility
      // model finishes a few seconds after the first send, and the gateway
      // pushes the row.
      //
      // Above the active-session guard, like the subtask count: a chat titled
      // while the user reads another one still belongs in the sidebar under
      // its real name. A missed push is not lost either — the roster read on
      // the next connect carries the same field.
      {
        const adopted = adoptableRowTitle({ ...payload, key: sessionKey });
        const resolved =
          adopted == null ? null : (resolveSessionId(sessionKey) ?? null);
        // A LABEL always applies. `mayRetitle` protects a name the user typed
        // here from being overwritten by the agent's GENERATED title, and it
        // is deliberately permanent — a chat someone named should never take
        // a generated one. A label is the opposite case: somebody named the
        // chat, on this device or another, and that is exactly what should
        // land. Without this the guard refused a rename made elsewhere for
        // the life of the tab.
        const isLabel =
          typeof (payload as { label?: unknown }).label === 'string' &&
          ((payload as { label?: string }).label ?? '').trim().length > 0;
        const titledId =
          resolved != null && (isLabel || mayRetitle(resolved))
            ? resolved
            : null;
        if (adopted != null && titledId != null) {
          setState((prev) => {
            const current = prev.sessions.find((s) => s.sessionId === titledId);
            if (current == null || current.title === adopted) return prev;
            return {
              ...prev,
              sessions: prev.sessions.map((s) =>
                s.sessionId === titledId ? { ...s, title: adopted } : s,
              ),
            };
          });
        }
      }

      if (!isFrameForActiveSession({ sessionKey })) return;
      const usage = parseSessionContextUsage(payload);
      if (usage == null) return;
      // Reconciled, not assigned. Every one of these events carries the whole
      // session snapshot — renames and pins included — and the gateway leaves
      // the fill at 0 for the life of a connection after a chat's first run,
      // so a blind assignment would drop a good reading back to "pending".
      // `reconcileSessionContextUsage` also returns the previous object by
      // identity when nothing changed, which keeps the no-op events (the
      // common case) from re-rendering every consumer.
      setState((prev) => {
        const contextUsage = reconcileSessionContextUsage(
          prev.contextUsage,
          usage,
        );
        return contextUsage === prev.contextUsage
          ? prev
          : { ...prev, contextUsage };
      });
      return;
    }

    // Generative UI render directive.
    if (evt.event === 'ui.render') {
      if (!options.generativeUiEnabled) {
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: generateMessageId('ui-disabled'),
              role: 'assistant' as const,
              content:
                '[Agent wanted to render UI — enable the terminal-generative-ui flag]',
              timestamp: appendedAfterClose(),
            },
          ],
        }));
        return;
      }

      const ui = evt.payload as UiRender | undefined;
      if (ui == null) return;
      const mode = ui.mode as string;
      if (mode !== 'catalog' && mode !== 'mcp-app') return;

      const messageId = generateMessageId('ui');
      const message: ChatMessage = {
        id: messageId,
        role: 'assistant',
        content: '',
        timestamp: appendedAfterClose(),
        ui,
      };

      setState((prev) => {
        const next: AgentState = {
          ...prev,
          processing: null,
          messages: [...closeStreamingMessages(prev.messages), message],
        };
        if ((ui.placement as string | undefined) === 'full-terminal') {
          return { ...next, fullTerminalUi: { messageId, ui } };
        }
        return next;
      });
      return;
    }

    // Handle app.register — register a third-party app tab
    if (evt.event === 'app.register') {
      const payload = evt.payload as
        | {
            id?: string;
            name?: string;
            icon?: string;
            url?: string;
            permissions?: string[];
          }
        | undefined;
      if (
        payload?.id != null &&
        payload.id !== 'agent-dashboard' &&
        payload.name != null &&
        payload.url != null &&
        // Reject non-http(s) URLs (e.g. javascript:, data:) before they
        // reach the app iframe `src` — the payload is untrusted gateway input.
        isSafeAppUrl(payload.url)
      ) {
        const manifest: AppManifest = {
          id: payload.id,
          name: payload.name,
          icon: payload.icon,
          url: payload.url,
          permissions: payload.permissions ?? [],
        };
        options.onAppRegister?.(manifest);
      }
      return;
    }

    // Handle app.remove — remove a third-party app tab
    if (evt.event === 'app.remove') {
      const payload = evt.payload as { id?: string } | undefined;
      if (payload?.id != null) {
        options.onAppRemove?.(payload.id);
      }
      return;
    }

    // A message that reached the transcript without being streamed.
    //
    // Two kinds arrive here, and they need opposite treatment.
    //
    // The first is a message the agent DELIVERED rather than spoke: it used
    // its own message tool — "here is the file you asked for", with a PDF
    // attached — and the gateway mirrored the finished message into the
    // transcript and announced it here, once, complete. Nothing handled this
    // event, so the message existed in the transcript and on the pod and
    // simply never appeared on screen: the user asked for a file, the agent
    // sent it, and the chat showed nothing until a reload. That one has to be
    // PUT ON SCREEN.
    //
    // The second is the user's own message, echoed back. Newer gateways
    // broadcast every turn that lands in a transcript, the user's included —
    // and that message is already on screen, painted the instant they pressed
    // Enter, because waiting for the round trip would make sending feel
    // broken. That one has to be RECOGNISED, not appended: appending it
    // showed every message the user sent twice, on every message, until they
    // reloaded the page.
    //
    // Telling them apart is `sendKeyFromRow` plus `options.localSendKeys` —
    // an exact match on the key this client put on the send, not a guess from
    // the text (two identical messages in a row are a real thing users do).
    //
    // Read through `projectGatewayHistory`, the same reader the transcript
    // uses, so a file attached to it is found wherever the gateway put it —
    // that reader knows all three shapes, and a second one here would be a
    // second place to fix every time a shape appears. With the same framing,
    // too: a turn goes out wrapped in the host's preamble, and a reader given
    // no framing to strip leaves that wrapper in the bubble.
    if (evt.event === 'session.message') {
      const payload = evt.payload as
        | {
            sessionKey?: string;
            messageId?: string;
            message?: Record<string, unknown>;
          }
        | undefined;
      const row = payload?.message as GatewayHistoryRow | undefined;
      if (row == null || typeof row !== 'object') return;

      const [projected] = projectGatewayHistory(
        [row],
        options.historyFraming ?? {},
      );
      if (projected == null) return;
      // The gateway's own id for the message, so the bubble is the same one a
      // later history read produces — and so a repeat of this event (a
      // reconnect replaying it) lands on the row already there instead of
      // beside it.
      const id = payload?.messageId ?? row.__openclaw?.id ?? projected.id;
      const sendKey = sendKeyFromRow(row);
      const localId =
        sendKey == null ? undefined : options.localSendKeys?.get(sendKey);
      // The run this row belongs to, when it belongs to one at all. See the
      // ownership rule below.
      const rowRunId = row.__openclaw?.runId;

      routeMessageFrame(payload ?? {}, (messages) => {
        if (messages.some((m) => m.id === id)) return messages;
        const mine =
          localId == null ? -1 : messages.findIndex((m) => m.id === localId);
        if (mine >= 0) {
          const localBubble = messages[mine];
          if (localBubble == null) return messages;
          const { unsent: _delivered, ...local } = localBubble;
          const adopted = [...messages];
          // Only the IDENTITY is adopted, not the content. The bubble already
          // holds exactly what the user typed; the row holds what went on the
          // wire, which is that text inside whatever framing this host adds.
          // Taking the row's version would make the bubble depend on the
          // unwrapping being perfect, for no gain.
          //
          // Files are taken only where the bubble has none — the local copy is
          // the picture the user actually chose, thumbnail and all, and the
          // row's is a URL on the pod that a later read will substitute
          // anyway.
          //
          // The mark meaning "the agent has no record of this" comes off: this
          // event IS the agent's record of it.
          adopted[mine] = {
            ...local,
            id,
            ...(local.attachments == null && projected.attachments != null
              ? { attachments: projected.attachments }
              : {}),
          };
          return adopted;
        }
        // THE RUN OWNS ITS BUBBLE, and this event does not.
        //
        // A streamed answer reaches the screen three times over: an `agent`
        // assistant delta, a `chat` delta, and a `chat` final. All three
        // address the run — the deltas accumulate into its bubble and the
        // final REPLACES it — which is why three frames carrying the same
        // text leave one bubble. This handler was the only one appending
        // without asking whose run it was, so every answer landed a fourth
        // time, as a second bubble, on every turn.
        //
        // Ignoring it is right rather than replacing with it, and the frame
        // order is why: taken from a live pod, this event arrives BEFORE the
        // run's `chat` final, not after. Anything written here would be
        // thrown away by that final a moment later — a flash and a revert
        // instead of a duplicate. The final carries the same content anyway,
        // attachments included, so there is nothing here it lacks.
        //
        // So this event is the authority for exactly one thing: a message
        // with NO run behind it, which is the agent delivering something
        // through its own message tool. Nothing streams for that, so
        // appending is its only route to the screen — and it is what this
        // handler was added for.
        //
        // Checked against a bubble the run actually has, rather than against
        // the run id alone, so a delivered file that did carry a run id would
        // still be shown. That fails towards showing a file rather than
        // hiding one, which is the safer direction for the case this handler
        // exists to serve.
        if (rowRunId != null && messages.some((m) => m.runId === rowRunId)) {
          return messages;
        }
        return [...messages, { ...projected, id }];
      });
      return;
    }

    // Handle "agent" events — streamed assistant responses
    if (evt.event === 'agent') {
      const payload = evt.payload as
        | {
            stream?: string;
            data?: {
              text?: string;
              delta?: string;
              state?: string;
              phase?: string;
              errorMessage?: string;
              /** Both spellings: nothing in the protocol pins which. */
              errorCode?: string;
              code?: string;
              tool?: string;
              toolName?: string;
              /** Reasoning frames: `text` replaces rather than appends. */
              replace?: boolean;
              /** CLI backends re-send the whole reasoning trace, not a delta. */
              isReasoningSnapshot?: boolean;
              widget?: {
                type: string;
                data: Record<string, unknown>;
              };
              /**
               * Files the AGENT produced in this turn — a chart it rendered, a
               * PNG it generated — as absolute paths inside the pod:
               *
               *   ["/home/node/.openclaw/workspace/tmp/circle-100x100.png"]
               *
               * A different thing from what the user attached, which the
               * transcript records as `media://inbound/<id>`. This field was
               * read by nothing, so the agent saying "here it is" arrived as
               * text with the file silently dropped.
               */
              mediaUrls?: readonly string[];
            };
            runId?: string;
            sessionKey?: string;
          }
        | undefined;

      if (payload == null) return;

      const { data } = payload;
      if (data == null) return;

      // Error state — surfaced as a transient toast, not a transcript
      // bubble: gateway/runtime failures ("Agent failed before reply", session
      // lock errors) are infrastructure noise, not conversation content, and
      // must not persist or sync as messages. The reducer still runs so
      // half-streamed bubbles close and the processing indicator clears.
      if (data.state === 'error') {
        if (payload.runId != null && isCancelled(payload.runId)) {
          return;
        }
        surfaceErrorFrame(
          payload,
          data.errorMessage ?? 'Unknown error',
          data.errorCode ?? data.code,
        );
        return;
      }

      // Widget payload — render as a structured widget
      if (data.widget != null) {
        if (payload.runId != null && isCancelled(payload.runId)) {
          return;
        }
        const { widget } = data;
        const widgetText = data.text;
        // A widget is ANSWER CONTENT, not the end of the run — the same
        // distinction the assistant-delta branch above turns on. These used to
        // clear the indicator, which ended the clock on the first tool-result
        // widget of a turn and then let the next state frame start a second
        // clock from zero, mid-turn. The run's own end signals (chat `final`,
        // a terminal state, `lifecycle.end`) are what stop it.
        //
        // agent_spec widget — render via AgentRenderer
        if (widget.type === 'agent_spec') {
          routeMessageFrame(payload, (messages) => [
            ...closeStreamingMessages(messages, payload.runId),
            {
              id: generateMessageId('agent-spec'),
              role: 'assistant' as const,
              content: '',
              timestamp: appendedAfterClose(),
              agentSpec: widget.data as unknown,
            },
          ]);
          return;
        }

        routeMessageFrame(payload, (messages) => [
          ...closeStreamingMessages(messages, payload.runId),
          {
            id: generateMessageId('widget'),
            role: 'assistant' as const,
            content: widgetText ?? '',
            timestamp: appendedAfterClose(),
            widgetType: widget.type,
            widgetData: widget.data,
          },
        ]);
        return;
      }

      // Streamed reasoning — the gateway pushes these as `agent` frames with
      // `stream: 'thinking'` and the same cumulative `text` + incremental
      // `delta` shape as the assistant stream. They flow whenever the model
      // thinks, independently of openclaw's `/reasoning` visibility level
      // (which only governs delivery to external channels), so this branch is
      // the terminal's only route to reasoning content.
      //
      // Deliberately does NOT clear `processing`: the Stop button is wired to
      // it, and a reasoning model can think for many seconds before its first
      // answer token. ChatView hides the duplicate spinner row while a
      // thinking block is live instead.
      if (payload.stream === 'thinking') {
        const { runId } = payload;
        if (runId != null && isCancelled(runId)) return;
        const suppressed = options.suppressedModelEchoRunIds;
        if (runId != null && suppressed?.has(runId) === true) return;
        // Two shapes arrive on this stream. Normally `delta` is the increment
        // and `text` the cumulative trace, so we append the delta. But
        // `isReasoningSnapshot` / `replace` frames (CLI backends) re-send the
        // whole trace, and some emitters send only `text` with no delta at
        // all — both mean "this IS the trace", so use `text` as a wholesale
        // replacement. Appending in those cases duplicated everything so far.
        const isSnapshot =
          data.replace === true || data.isReasoningSnapshot === true;
        const hasDelta =
          !isSnapshot && data.delta != null && data.delta.length > 0;
        const text = hasDelta ? data.delta! : data.text;
        if (text == null || text.length === 0) return;
        const mode = hasDelta ? 'append' : 'replace';
        const runEnded = isRunEnded(runId, payload);
        routeMessageFrame(
          payload,
          (messages) =>
            applyThinkingUpdate(
              messages,
              runId,
              text,
              mode,
              Date.now(),
              runEnded,
            ),
          {
            // The in-flight row's token estimate is sized from how much
            // reasoning has arrived, so it is counted here — routed rather
            // than written straight to state, so a backgrounded run keeps
            // counting into its stashed indicator and shows a figure that
            // moved while you were away rather than one frozen at the moment
            // you left. That is exactly the long reasoning run this exists
            // for.
            processing: (prev) => {
              if (prev == null || runEnded) return prev;
              // Same "one run owns the row" rule as the end-of-run clears: a
              // delta from another run must not change what this one shows.
              const mine =
                prev.runId == null || runId == null || prev.runId === runId;
              if (!mine) return prev;
              // A `replace` frame carries the WHOLE trace, not an increment,
              // so the count is SET from it. Adding would re-count everything
              // so far on every snapshot — the same double-count the message
              // path avoids by replacing rather than appending.
              return {
                ...prev,
                thinkingChars:
                  mode === 'append'
                    ? prev.thinkingChars + text.length
                    : text.length,
              };
            },
          },
        );
        return;
      }

      // Streaming assistant text — use delta to update the last message
      if (payload.stream === 'assistant' && data.delta != null) {
        const { runId } = payload;
        if (runId != null && isCancelled(runId)) {
          return;
        }
        const suppressed = options.suppressedModelEchoRunIds;
        if (runId != null && suppressed?.has(runId) === true) {
          // First delta on this run was a `/model` echo; drop the rest.
          return;
        }
        const deltaText = data.delta;
        // Files the agent produced in this turn. They arrive on the same frame
        // as the text that announces them ("here it is"), and nothing read the
        // field — so the answer landed with its file dropped.
        const produced = attachmentsFromMediaPaths(data.mediaUrls);
        routeMessageFrame(
          payload,
          (messages) => {
            // Answer text means no call is in flight: collapse the thinking
            // block and end tool activity, or the header shimmers "Running X"
            // for the whole answer and the next call to it reads as a repeat.
            const base = endToolActivity(endThinking(messages, runId), runId);
            const updated = updateStreamingMessage(base, runId, (message) => ({
              ...message,
              content: message.content + deltaText,
              // Merged rather than replaced: a turn can produce a second file
              // in a later frame, and a re-sent frame must not double the
              // first one.
              ...(produced.length > 0
                ? {
                    attachments: mergeAttachments(
                      message.attachments,
                      produced,
                    ),
                  }
                : {}),
            }));
            if (updated != null) return updated;
            // First delta of a new streaming message. If it looks like
            // the gateway's `/model <id>` echo, skip creating the
            // bubble — the provider appends its own verified
            // confirmation in `setSelectedModel`. Mark the runId so
            // subsequent deltas for the same run also get dropped.
            if (isModelSwitchEcho(deltaText)) {
              if (runId != null) suppressed?.add(runId);
              // Drop the run's thinking block too, as the `chat` echo branch
              // does. Returning `messages` left it live, and every later
              // frame for the run — terminators included — is dropped by the
              // suppression guard above, so nothing could ever close it: a
              // permanently-live block that suppresses the processing row for
              // the rest of the transcript's life via `hasLiveThinking`.
              if (runId == null) return base;
              return base.filter(
                (m) =>
                  !(
                    m.role === 'assistant' &&
                    m.runId === runId &&
                    m.thinking != null
                  ),
              );
            }
            return [
              ...(runId == null ? closeRunlessMessages(base) : base),
              {
                id: generateMessageId('assistant'),
                role: 'assistant' as const,
                content: deltaText,
                // Only nudged when this branch actually closed something —
                // with a runId nothing is closed here, and a bubble's start
                // time should be its own, not two ticks ahead of it.
                timestamp: runId == null ? appendedAfterClose() : Date.now(),
                isStreaming: true,
                runId,
                ...(produced.length > 0 ? { attachments: produced } : {}),
              },
            ];
          },
          // Deliberately NOT `{ processing: null }`. The indicator used to be
          // torn down here, which ended the run clock the moment the first
          // character of the answer appeared — so the timer covered the wait
          // but not the response. It now survives to the run's own end signal
          // (chat `final`, a terminal state, or the lifecycle-end clear
          // below), and only changes what it displays.
          //
          // Nothing is recorded about "the answer has started" either: the
          // streaming message this reducer just produced IS that fact, and
          // ChatView reads it from there. A flag set here would be wrong for
          // the `/model` echo above, whose deltas never become a bubble.
        );
        return;
      }

      // Run finished. This is a SAFETY NET, not the primary clear: chat
      // `final` and the terminal `data.state` values both already clear the
      // indicator, and normally get there first. But now that the assistant
      // stream no longer tears it down, a gateway build that skips those
      // frames would leave the clock running forever, and lifecycle end is
      // the one signal every build in evidence sends.
      //
      // Routed rather than applied straight to state, for the same reason the
      // terminal-state branch below is: a run can finish in a session the user
      // has switched away from, and its stashed indicator has to be dropped
      // too or switching back shows a clock counting a run that ended. The
      // streaming bubble is closed on the way out for the same reason — on a
      // build that skips `final`, this is the only frame that can do it — and
      // `onRunEnd` fires so run ownership is released exactly once.
      //
      // The indicator is only cleared when this frame is FOR the run the
      // indicator belongs to. A trailing end from the previous turn resolves
      // to this session through the last-send fallback, and clearing on it
      // would kill the row for the run that just started — which nothing
      // would bring back, since only a state frame raises one.
      if (payload.stream === 'lifecycle' && data.phase === 'end') {
        routeMessageFrame(
          payload,
          (messages) => endRunMessages(messages, payload.runId),
          { processing: endRunPatch(payload.runId) },
        );
        // Retired, not released, for the reason the chat-`final` branch gives:
        // a straggler still carries this runId, and an unowned one lands on
        // whichever chat is open.
        recordRunEnd(payload);
        return;
      }

      // Streaming reasoning text. Counted, never rendered: the only thing
      // taken from this stream is how much of it arrived, which sizes the
      // live token estimate on the in-flight row while the user waits.
      //
      // This is the ONLY frame that reports progress during the thinking
      // phase. Without it a reasoning run is silent from `lifecycle.start`
      // until the first assistant delta — measured at 32s on a real run — so
      // the elapsed timer is the only thing moving.
      // Processing-state indicator. The gateway sends `data.state` strings
      // like `thinking`, `tool_use`, `planning`, etc. We don't have the
      // canonical vocabulary locally (OpenClaw lives in a sibling repo), so
      // we map known values to friendly labels and titleize anything else —
      // the UI degrades gracefully as the gateway adds new states.
      if (typeof data.state === 'string' && data.state.length > 0) {
        // A trailing frame from a run that has already finished must not raise
        // or rename a row. This is what protects the row raised optimistically
        // by a local send, which has no runId yet and would otherwise adopt
        // the id of the previous turn.
        // …and neither must a run the user has stopped. `stopProcessing`
        // records the cancellation, and the comment on the reasoning guard
        // above says why this window is the normal case rather than an edge:
        // a cancelled run keeps streaming for a moment. Without this, a late
        // state frame re-raised the row for a run the user had killed — and
        // that run's own `final` could never clear it, because the final
        // branch returns early for cancelled runs.
        if (isEndedRun(payload.runId)) return;
        if (payload.runId != null && isCancelled(payload.runId)) return;
        // A muted run may END things but not RAISE or RENAME the row: a
        // seed's `state: thinking` used to name the row `sendMessage` had
        // just raised, and its terminator then cleared it by exact match.
        const mutedRun =
          payload.runId != null &&
          options.suppressedModelEchoRunIds?.has(payload.runId) === true;
        const stateKey = data.state.toLowerCase();
        if (TERMINAL_PROCESSING_STATES.has(stateKey)) {
          // Terminal state is also our most reliable stream-end signal —
          // some gateway builds skip `agent.done`. Flip any streaming
          // message for this run off so the dots stop animating.
          routeMessageFrame(
            payload,
            (messages) => endRunMessages(messages, payload.runId),
            {
              processing: mutedRun
                ? endMutedRunPatch(payload.runId)
                : endRunPatch(payload.runId),
            },
          );
          recordRunEnd(payload);
          return;
        }
        if (mutedRun) return;
        const label = resolveProcessingLabel(data.state);
        const rawDetail = data.toolName ?? data.tool ?? data.text;
        const detail =
          rawDetail != null && rawDetail.length > 0 ? rawDetail : undefined;
        if (!isFrameForActiveSession(payload)) {
          return;
        }
        setState((prev) => {
          // The last message, stepping over tool logs. Both guards below read
          // this positionally, assuming a live bubble or block is last — which
          // a tool log breaks by being appended after them.
          //
          // Only tool logs are stepped over. Scanning for the last LIVE message
          // instead is unbounded and not run-scoped: a bubble left open by a
          // dropped terminator would be found forever and pin the label.
          let lastIndex = prev.messages.length - 1;
          while (
            lastIndex >= 0 &&
            prev.messages[lastIndex]?.toolCalls != null
          ) {
            lastIndex -= 1;
          }
          const lastMsg = lastIndex >= 0 ? prev.messages[lastIndex] : undefined;
          // Mid-answer, the text is the signal and the row keeps out of it.
          if (lastMsg?.isStreaming === true) return prev;
          // A live thinking block sits at the end of the transcript for the
          // whole reasoning phase, and committing a fresh `processing` for
          // every repeat `state` frame behind it re-runs the transcript memo
          // for seconds at a time. So an UNCHANGED state commits nothing.
          //
          // A changed one still lands, though: the block says the model is
          // working, but only the label says WHAT — "Calling tool —
          // paradex_get_positions" is precisely the transition worth showing,
          // and it arrives while the block is live. Blocking every relabel to
          // avoid the repeats threw that away too.
          if (
            lastMsg?.isThinking === true &&
            prev.processing?.label === label &&
            prev.processing?.detail === detail
          ) {
            return prev;
          }
          // The row belongs to ONE run, and only that run may change it.
          //
          // Without this, a state frame from any other run renamed the
          // indicator, and every end signal for the run it actually belongs to
          // was then skipped by every terminator — the row counted forever
          // with the composer pinned to Stop. That is strictly worse than the
          // unscoped clear it replaced, which at least always ended.
          //
          // The guard above is not enough on its own: any mid-run widget
          // appends a non-streaming message and reopens the window.
          if (
            prev.processing?.runId != null &&
            payload.runId != null &&
            prev.processing.runId !== payload.runId
          ) {
            return prev;
          }
          return {
            ...prev,
            processing: {
              label,
              detail,
              // Carry the existing stamp forward: a state change mid-run
              // (thinking -> tool_use -> writing) must not restart the clock.
              // Only a run that began without a local send (e.g. resumed from
              // another client) stamps here.
              startedAt: prev.processing?.startedAt ?? Date.now(),
              // Carried for the same reason as `startedAt`: a state change
              // mid-run must not reset what the run has already produced.
              thinkingChars: prev.processing?.thinkingChars ?? 0,
              // An unnamed row adopts the first run that is OURS to name it; a
              // named one keeps its own. Never the other way round — the
              // frame's id winning is what let a foreign run rename the row.
              // An unnamed row is safe (see `endRunPatch`), so refusing to name
              // it costs nothing when we cannot be sure.
              runId:
                prev.processing?.runId ??
                (namesOurRun(payload.runId) ? payload.runId : undefined),
            },
          };
        });
        // Fold this state into the run's tool log. Its own `setState`, and
        // AFTER the one above: it appends a message, and that updater reasons
        // about the transcript's live tail. Separate rather than a branch of
        // it because that one returns early on frames the log still wants.
        //
        // Active-session only, like the label — routing state frames per
        // session would put a localStorage read behind every gateway tick.
        {
          const isToolState = TOOL_PROCESSING_STATES.has(stateKey);
          const toolName = isToolState ? toolNameFromFrame(data) : undefined;
          const stampedAt = Date.now();
          setState((prev) => {
            // Three cases: undefined `toolName` means either "not a tool
            // frame" (ends activity) or "a tool frame with an unusable name",
            // which must change nothing — the previous frame's call is still
            // running, and clearing here counted it twice.
            let next: readonly ChatMessage[];
            if (toolName != null) {
              next = applyToolCallUpdate(
                prev.messages,
                payload.runId,
                toolName,
                stampedAt,
              );
            } else if (isToolState) {
              next = prev.messages;
            } else {
              next = endToolActivity(prev.messages, payload.runId);
            }
            return next === prev.messages ? prev : { ...prev, messages: next };
          });
        }
        return;
      }

      return;
    }

    // Handle "chat" events — structured message delivery (delta / final / error).
    if (evt.event === 'chat') {
      const payload = evt.payload as
        | {
            state?: string;
            text?: string;
            errorMessage?: string;
            /** As on the `agent` branch: either spelling of the wire code. */
            errorCode?: string;
            code?: string;
            runId?: string;
            sessionKey?: string;
            message?: {
              role?: string;
              content?: ReadonlyArray<{
                type?: string;
                text?: string;
                mimeType?: string;
              }>;
              timestamp?: number;
            };
          }
        | undefined;

      // Error state — transient toast, not a transcript bubble (see the
      // matching `agent` error branch above).
      if (payload?.state === 'error') {
        if (payload.runId != null && isCancelled(payload.runId)) {
          return;
        }
        surfaceErrorFrame(
          payload,
          payload.errorMessage ?? 'Unknown error',
          payload.errorCode ?? payload.code,
        );
        return;
      }

      // Read through the SAME reader the transcript uses. This used to have
      // its own: text blocks joined, `type === 'image'` mapped to a bare
      // `{type, mimeType}`. That knew none of the shapes the other one does —
      // not the address a file is served from, not its name, not a document
      // nested under an `attachment` key — so the same message rendered one
      // way live and another way on reopening, and a file could appear twice
      // because the two readers identified it differently.
      const msgContent = payload?.message?.content;
      if (msgContent != null && msgContent.length > 0) {
        const read = readGatewayRowContent({
          content: msgContent as GatewayHistoryRow['content'],
        });
        const text = read.text;
        const attachments = read.attachments;

        if (text.length > 0 || attachments != null) {
          const runId = payload?.runId;
          const ts = payload?.message?.timestamp ?? Date.now();

          if (runId != null && isCancelled(runId)) {
            return;
          }

          // Gateway `/model <id>` echo — the provider appends its own
          // verified confirmation client-side, so drop the duplicate.
          // `chat` event text is cumulative (each delta carries the full
          // message so far), so the prefix check matches reliably even
          // mid-stream. Also remove any streaming bubble we created from
          // an earlier delta before we recognized the prefix.
          const suppressed = options.suppressedModelEchoRunIds;
          if (isModelSwitchEcho(text) && payload != null) {
            if (runId != null) suppressed?.add(runId);
            retireMutedRun(payload, runId);
            return;
          }
          if (
            runId != null &&
            payload != null &&
            suppressed?.has(runId) === true
          ) {
            // Muted, not absent — the run still has to END, or on a gateway
            // whose only terminator is chat `final` a late `state` frame
            // raises a row for a finished run that nothing can clear.
            retireMutedRun(payload, runId);
            return;
          }

          if (payload?.state === 'delta') {
            routeMessageFrame(
              payload,
              (messages) => {
                // See the `agent` delta branch above.
                const base = endToolActivity(
                  endThinking(messages, runId),
                  runId,
                );
                const updated = updateStreamingMessage(
                  base,
                  runId,
                  (message) => ({
                    ...message,
                    content: text,
                    ...(attachments != null ? { attachments } : {}),
                  }),
                );
                if (updated != null) return updated;
                return [
                  ...(runId == null ? closeRunlessMessages(base) : base),
                  {
                    id: generateMessageId('chat-delta'),
                    role: 'assistant' as const,
                    content: text,
                    timestamp: ts,
                    isStreaming: true,
                    runId,
                    ...(attachments != null ? { attachments } : {}),
                  },
                ];
              },
              // Same reasoning as the `agent` assistant stream: this is the
              // answer arriving, not the run ending, so the indicator stays
              // up and only switches to a bare clock. Gateways send both
              // shapes, so BOTH had to stop tearing it down or the timer
              // still dies at the first character.
            );
            return;
          }

          if (payload?.state === 'final') {
            routeMessageFrame(
              payload,
              (messages) => {
                const replacements: ChatMessage[] = [];
                const extracted = tryExtractAgentSpec(text);
                if (extracted != null) {
                  if (extracted.preamble.length > 0) {
                    replacements.push({
                      id: generateMessageId('chat-text'),
                      role: 'assistant' as const,
                      content: extracted.preamble,
                      timestamp: ts,
                      runId,
                    });
                  }
                  replacements.push({
                    id: generateMessageId('agent-spec'),
                    role: 'assistant' as const,
                    content: '',
                    timestamp: ts,
                    runId,
                    agentSpec: extracted.spec,
                  });
                  return replaceRunMessages(messages, runId, replacements);
                }
                replacements.push({
                  id: generateMessageId('chat-final'),
                  role: 'assistant' as const,
                  content: text,
                  timestamp: ts,
                  runId,
                  ...(attachments != null ? { attachments } : {}),
                });
                return replaceRunMessages(messages, runId, replacements);
              },
              { processing: endRunPatch(runId) },
            );
            // The primary terminator, and it was long the one path that
            // never said so: run ownership leaked an entry per turn (the leak
            // `onRunEnd` exists to prevent), and nothing recorded the run as
            // finished, so its trailing frames could still land on the next
            // turn's row.
            //
            // A chat `final` IS a terminator — it replaces the run's messages
            // and clears `processing` — so it RETIRES the run, without which a
            // late reasoning frame could open a live block nothing would
            // close, on any gateway that skips `agent.done` (which this
            // file's own comments say cannot be assumed).
            //
            // It does NOT release ownership: trailing frames still carry the
            // runId, and an unowned one lands on whichever chat is open. The
            // cost is that on a gateway which only ever sends chat `final`,
            // neither `runOwnershipRef` nor `suppressedModelEchoRunIds` is
            // ever pruned — both are released under the same guard — see the
            // note on `endedRunIdsRef` in AgentProvider.
            recordRunEnd(payload);
            return;
          }
        }
      }

      // A `final` with no content at all still ends the run. The whole branch
      // above sits behind "has text or attachments", so an empty final used to
      // fall out here having cleared nothing — leaving the row up until some
      // later frame happened to stop it. It is the PRIMARY clear the
      // lifecycle-end safety net is written as a backstop to, so it has to
      // fire even when there is nothing to render.
      if (payload?.state === 'final') {
        const runId = payload.runId;
        if (runId != null && isCancelled(runId)) return;
        routeMessageFrame(
          payload,
          (messages) => endRunMessages(messages, runId),
          { processing: endRunPatch(runId) },
        );
        recordRunEnd(payload);
      }
      return;
    }

    // Handle structured approval request events from gateway
    if (
      evt.event === 'agent.approval' ||
      evt.event === 'approval.request' ||
      evt.event === 'tool.approval'
    ) {
      const payload = evt.payload as
        | { id?: string; command?: string; description?: string }
        | undefined;
      if (payload?.command != null) {
        const approval: ApprovalRequest = {
          approvalId: payload.id ?? generateMessageId('approval'),
          command: payload.command,
          description: payload.description,
          state: 'pending',
        };
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: generateMessageId('approval'),
              role: 'assistant' as const,
              content: payload.description ?? `Approve: ${payload.command}`,
              timestamp: appendedAfterClose(),
              approval,
              // This bubble IS the card, not an answer with an approval
              // recognised in it. The gateway has no row for it, and that is
              // what exempts it from replacement on a transcript read — see
              // `ChatMessage.approvalCard`.
              approvalCard: true,
            },
          ],
        }));
      }
      return;
    }

    // Handle "chat.done" / "agent.done" — mark streaming as complete.
    // Also detect text-based approval requests in completed messages.
    if (evt.event === 'agent.done' || evt.event === 'chat.done') {
      const payload = evt.payload as
        | { runId?: string; sessionKey?: string }
        | undefined;
      const runId = payload?.runId;
      if (runId != null && isCancelled(runId)) {
        return;
      }
      routeMessageFrame(
        payload ?? {},
        (messages) => {
          const closed = endRunMessages(messages, runId);
          // The run's ANSWER bubble. Steps over the sibling records a run also
          // leaves behind, which carry no `content` for the extraction below.
          // The tool log is the one that bites: it lands AFTER the answer
          // whenever the first tool call follows the first answer delta.
          const isAnswerRecord = (m: ChatMessage | undefined): boolean =>
            m?.role === 'assistant' &&
            m.thinking == null &&
            m.toolCalls == null;
          let targetIndex = -1;
          if (runId != null) {
            for (let i = closed.length - 1; i >= 0; i -= 1) {
              if (isAnswerRecord(closed[i]) && closed[i]?.runId === runId) {
                targetIndex = i;
                break;
              }
            }
          } else {
            // Run-less: the old read was "the last message"; keep that and
            // only step over records that are never an answer.
            for (let i = closed.length - 1; i >= 0; i -= 1) {
              if (closed[i]?.toolCalls == null) {
                targetIndex = i;
                break;
              }
            }
          }
          const targetMsg = targetIndex >= 0 ? closed[targetIndex] : undefined;

          if (
            targetMsg != null &&
            targetMsg.role === 'assistant' &&
            targetMsg.agentSpec == null
          ) {
            const extracted = tryExtractAgentSpec(targetMsg.content);
            if (extracted != null) {
              const replacements: ChatMessage[] = [];
              if (extracted.preamble.length > 0) {
                replacements.push(
                  {
                    ...targetMsg,
                    content: extracted.preamble,
                    agentSpec: undefined,
                  } as ChatMessage,
                  {
                    id: generateMessageId('agent-spec'),
                    role: 'assistant' as const,
                    content: '',
                    timestamp: targetMsg.timestamp,
                    runId: targetMsg.runId,
                    agentSpec: extracted.spec,
                  },
                );
              } else {
                replacements.push({
                  id: generateMessageId('agent-spec'),
                  role: 'assistant' as const,
                  content: '',
                  timestamp: targetMsg.timestamp,
                  runId: targetMsg.runId,
                  agentSpec: extracted.spec,
                });
              }
              return [
                ...closed.slice(0, targetIndex),
                ...replacements,
                ...closed.slice(targetIndex + 1),
              ];
            }

            if (targetMsg.approval == null) {
              const approval = detectApprovalRequest(targetMsg.content);
              if (approval != null) {
                return [
                  ...closed.slice(0, targetIndex),
                  { ...targetMsg, approval },
                  ...closed.slice(targetIndex + 1),
                ];
              }
            }
          }
          return closed;
        },
        { processing: endRunPatch(runId) },
      );
      recordRunEnd(payload ?? {}, true);
      return;
    }

    // Silently ignore other events (health, tick, presence, etc.)
    return;
  }

  // At this point frame must be a GatewayResponse (events handled above).
  // Responses whose id matched a pending request were already resolved/rejected
  // in createAgentWebSocket (never reaches here). What arrives here are
  // "orphan" error responses — typically infrastructure-level failures relayed
  // by the gateway-proxy (e.g. "gateway unreachable") that have no matching
  // in-flight request. These are connection-layer concerns, not conversation
  // content — the reconnect machinery and status banners already surface them.
  // Rendering them as chat bubbles pollutes the conversation, so drop them.
  {
    const res = frame;
    if (!res.ok && res.error != null) {
      const msg = res.error.message ?? 'Request failed';
      // `details.code` first, as `parseProtocolMismatch` does: the top-level
      // one is a coarse envelope verdict (INVALID_REQUEST) while the specific
      // failure is inside, and classification wants the specific one.
      const detailCode = (res.error.details as { code?: unknown } | undefined)
        ?.code;
      const code = typeof detailCode === 'string' ? detailCode : res.error.code;
      // eslint-disable-next-line no-console
      console.warn(
        '[terminal] orphan error response (not rendered):',
        msg,
        res,
      );
      // Surface as a transient error (auto-dismissed) + clear processing.
      setState((prev) => ({
        ...prev,
        processing: null,
        transientError: msg,
      }));
      options.onTransientError?.(msg, code);
    }
  }
}

/**
 * Try to extract a JSON agent-spec from streamed message content.
 *
 * The LLM sometimes emits the component-tree JSON inline as plain text
 * (instead of a structured `widget` frame). When that happens the chat
 * renders raw JSON. This helper finds the outermost `{…}` blob whose
 * shape matches an agent spec (`layout`, `children`) and returns it
 * together with any leading prose so the caller can split the message.
 * The spec is opaque (`unknown`) — the host renderer narrows it.
 */
function tryExtractAgentSpec(
  text: string,
): { spec: unknown; preamble: string } | null {
  // Find the first top-level '{' that could start a JSON object.
  const start = text.indexOf('{');
  if (start === -1) return null;

  // Walk from the *end* to find the matching closing '}'.
  const end = text.lastIndexOf('}');
  if (end <= start) return null;

  const candidate = text.slice(start, end + 1);
  try {
    const parsed: unknown = JSON.parse(candidate);
    if (
      parsed != null &&
      typeof parsed === 'object' &&
      'layout' in (parsed as Record<string, unknown>) &&
      'children' in (parsed as Record<string, unknown>) &&
      Array.isArray((parsed as Record<string, unknown>).children)
    ) {
      return {
        spec: parsed,
        preamble: text.slice(0, start).trim(),
      };
    }
  } catch {
    // Not valid JSON — fall through.
  }
  return null;
}

/**
 * Detect approval-request patterns in agent text messages.
 * Matches patterns like:
 *   "please approve the `some command` command"
 *   "approval needed — please approve the `...` command in the UI"
 * Extracts the command from backticks.
 */
function detectApprovalRequest(text: string): ApprovalRequest | null {
  // Match "approve" near a backtick-wrapped command
  const approvePattern =
    /(?:please\s+)?approv(?:e|al\s+(?:needed|required))[^`]*`([^`]+)`/iu;
  const match = approvePattern.exec(text);
  if (match != null) {
    const desc = text.replace(approvePattern, '').trim();
    return {
      approvalId: generateMessageId('text-approval'),
      command: match[1] ?? '',
      description: desc.length > 0 ? desc : undefined,
      state: 'pending',
    };
  }
  return null;
}
